import { For, Show } from "solid-js";
import { produce, type SetStoreFunction } from "solid-js/store";
import type { EditableMetric } from "./draftDocument";

export function MetricDraftCard(props: {
  metric: EditableMetric;
  index: () => number;
  setMetrics: SetStoreFunction<EditableMetric[]>;
}) {
  return (
    <details class="panel metric-card">
      <summary>
        <strong>{props.metric.label}</strong>{" "}
        <span class="mono">{props.metric.key}</span>{" "}
        <span class="badge">{props.metric.pillar}</span>
        <Show when={props.metric.checks.length === 0}>
          {" "}
          <span
            class="badge badge-sev sev-medium"
            title="No checks: this metric is out of checklist scoring and coverage under this draft."
          >
            not checklist-scored
          </span>
        </Show>
      </summary>
      <div class="metric-card-body">
        <label class="landing-sub">
          Description
          <textarea
            class="editor-input"
            rows={2}
            value={props.metric.description}
            onInput={(event) =>
              props.setMetrics(
                props.index(),
                "description",
                event.currentTarget.value,
              )
            }
          />
        </label>
        <div class="landing-head-row">
          <For each={["target", "good", "watch"] as const}>
            {(field) => (
              <label class="landing-sub">
                {field}{" "}
                <input
                  class="editor-input"
                  type="number"
                  style={{ width: "5rem" }}
                  value={props.metric.thresholds[field]}
                  onInput={(event) =>
                    props.setMetrics(
                      props.index(),
                      "thresholds",
                      field,
                      Number(event.currentTarget.value),
                    )
                  }
                />
              </label>
            )}
          </For>
        </div>
        <For each={props.metric.checks}>
          {(check, checkIndex) => (
            <div class="check-box">
              <div class="landing-head-row">
                <span class="mono">{check.checkKey}</span>
                <label class="landing-sub">
                  points{" "}
                  <input
                    class="editor-input"
                    type="number"
                    min={1}
                    style={{ width: "4rem" }}
                    value={check.points}
                    onInput={(event) =>
                      props.setMetrics(
                        props.index(),
                        "checks",
                        checkIndex(),
                        "points",
                        Number(event.currentTarget.value),
                      )
                    }
                  />
                </label>
                <button
                  class="btn"
                  type="button"
                  onClick={() =>
                    props.setMetrics(
                      props.index(),
                      "checks",
                      produce((checks) => {
                        checks.splice(checkIndex(), 1);
                      }),
                    )
                  }
                >
                  Remove
                </button>
              </div>
              <textarea
                class="editor-input"
                rows={2}
                value={check.question}
                onInput={(event) =>
                  props.setMetrics(
                    props.index(),
                    "checks",
                    checkIndex(),
                    "question",
                    event.currentTarget.value,
                  )
                }
              />
              <input
                class="editor-input"
                placeholder="Evidence hint"
                value={check.evidenceHint}
                onInput={(event) =>
                  props.setMetrics(
                    props.index(),
                    "checks",
                    checkIndex(),
                    "evidenceHint",
                    event.currentTarget.value,
                  )
                }
              />
            </div>
          )}
        </For>
        <Show when={props.metric.subcriteria.length === 0}>
          <button
            class="btn"
            type="button"
            onClick={() =>
              props.setMetrics(
                props.index(),
                "checks",
                produce((checks) => {
                  checks.push({
                    checkKey: `draft_check_${checks.length + 1}`,
                    points: 1,
                    question: "",
                    evidenceHint: "",
                  });
                }),
              )
            }
          >
            Add check
          </button>
        </Show>
        <For each={props.metric.subcriteria}>
          {(sub, subIndex) => (
            <div class="check-box">
              <p>
                <strong>{sub.label}</strong> <span class="mono">{sub.key}</span>
              </p>
              <label class="landing-sub">
                Definition
                <textarea
                  class="editor-input"
                  rows={3}
                  value={sub.definition}
                  onInput={(event) =>
                    props.setMetrics(
                      props.index(),
                      "subcriteria",
                      subIndex(),
                      "definition",
                      event.currentTarget.value,
                    )
                  }
                />
              </label>
              <label class="landing-sub">
                Evidence guidance
                <textarea
                  class="editor-input"
                  rows={2}
                  value={sub.evidenceGuidance}
                  onInput={(event) =>
                    props.setMetrics(
                      props.index(),
                      "subcriteria",
                      subIndex(),
                      "evidenceGuidance",
                      event.currentTarget.value,
                    )
                  }
                />
              </label>
            </div>
          )}
        </For>
      </div>
    </details>
  );
}
